import classNames from "classnames";
import cn from "classnames";
import { FC, type ReactNode, useRef, useState, useLayoutEffect, type HTMLAttributes } from "react";

const calculateDistance = ({
  distanceFromCenter,
  degree,
  w,
  h,
}: {
  distanceFromCenter: number;
  degree: number;
  w: number;
  h: number;
}) => {
  const distancePx = (distanceFromCenter / 100) * (Math.min(w, h) / 2);
  const angleRad = (degree * Math.PI) / 180;
  const ribbonWidth =
    Math.abs(w * Math.cos(angleRad)) + Math.abs(h * Math.sin(angleRad));
  const ribbonCenterX = w / 2 + distancePx * -Math.sin(angleRad);
  const ribbonCenterY = h / 2 + distancePx * Math.cos(angleRad);
  return {
    ribbonCenterX,
    ribbonCenterY,
    ribbonWidth,
  };
};

const Badge: FC<{
  children: ReactNode;
  degree?: number;
  title?: ReactNode;
  className?:string;
  bannerClassname?:string,
  distanceFromCenter?: number;
} & Omit<HTMLAttributes<HTMLDivElement>, "title">> = ({
  children,
  title,
  degree = -45,
  distanceFromCenter = -70,
  className,
  bannerClassname,
  ...props
}) => {
  const childRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!childRef.current) return;
    const el = childRef.current;
    const observer = new ResizeObserver(([entry]) => {
      const { inlineSize: w, blockSize: h } = entry.contentBoxSize[0];
      setDims({ w, h });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { w, h } = dims;

  if (!title)
    return (
      <div className={cn("justify-center relative inline-flex items-center", className)} {...props}>
        {children}
      </div>
    );
  const { ribbonCenterX, ribbonWidth, ribbonCenterY } = calculateDistance({
    degree,
    distanceFromCenter,
    w,
    h,
  });
  return (
    <div
      ref={childRef}
      className={cn("justify-center relative inline-flex items-center",className)}
      {...props}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div
          className={classNames("absolute z-10 flex items-center justify-center py-0.5",bannerClassname)}
          style={{
            width: ribbonWidth,
            left: ribbonCenterX,
            top: ribbonCenterY,
            transform: `translate(-50%, -50%) rotate(${degree}deg)`,
          }}
        >
          {title}
        </div>
      </div>
      {children}
    </div>
  );
};

export default Badge;
